import { useThree } from '@react-three/fiber';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { ISelectedSimpleMeasurement } from '../../@types/ContextProviderRelated';
import { EInteractionMode } from '../../@types/Enums';
import SimpleMeasurementModel from '../../models/standard/SimpleMeasurementModel';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { useDrawing } from '../../provider/DrawingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useSelection } from '../../provider/SelectionProvider';
import { useSingleSelectContext } from '../../provider/SingleSelectProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUserDataContext } from '../../provider/UserDataProvider';
import CameraPositionSprite from './CameraPositionSprite';
import Hotspot from './Hotspot';
import PreviewHotspot from './PreviewHotspot';
import SimpleMeasurement from './SimpleMeasurement';

const Annotations: React.FC = () => {
  const { camera, gl, scene } = useThree();
  const { interactionMode, setInteractionMode } = useToolbarContext();
  const { isDrawing } = useDrawing();
  const { setCurrentlySelected, items } = useSingleSelectContext();
  const { currentUserData } = useUserDataContext();
  const { showHotspots, setShowHotspots, showMeasurements, setShowMeasurements, showCameraPositionSprites, setShowCameraPositionSprites } = useMultiSelectToggleContext();
  const { hotspots, addHotspot, cameraPositionSprites, updateHotspotAttachment, updateMeasurementPosition, deleteHotspot, measurements, addMeasurement } = useAnnotations();
  const { handleSimpleMeasurementClick, selectedSimpleMeasurement, handleHotspotClick, selectedHotspot } = useSelection();
  const [newHotspotId, setNewHotspotId] = useState<number | null>(null);
  const [startPoint, setStartPoint] = useState<THREE.Vector3 | null>(null);
  const [tempEndPoint, setTempEndPoint] = useState<THREE.Vector3 | null>(null);
  const [tempHotspotPosition, setTempHotspotPosition] = useState<THREE.Vector3 | null>(null);
  const hotspotGroupRef = useRef<THREE.Group>(new THREE.Group());
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  useEffect(() => {
    if (interactionMode !== EInteractionMode.MEASURE || startPoint === null) {
      setTempEndPoint(null);
    }
  }, [interactionMode, startPoint]);

  useEffect(() => {
    if (interactionMode === EInteractionMode.MEASURE && !showMeasurements) {
      setShowMeasurements(true);
    }
    if (interactionMode !== EInteractionMode.ATTACHMENT) {
      setTempHotspotPosition(null);
    }
  }, [interactionMode]);

  const handleCanvasClick = useCallback(
    (event: MouseEvent) => {
      if (event.button !== 0) return;

      const mouse = new THREE.Vector2((event.clientX / window.innerWidth) * 2 - 1, -(event.clientY / window.innerHeight) * 2 + 1);

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      if (intersects.length === 0) return;
      const intersection = intersects[0].point;
      intersection.z = 0;

      if (interactionMode === EInteractionMode.MEASURE) {
        event.stopPropagation();
        if (!startPoint) {
          setStartPoint(intersection.clone());
        } else {
          const newMeasurement = new SimpleMeasurementModel(
            measurements.length + 1,
            startPoint,
            intersection.clone(),
            '#f89306',
            currentUserData?.id || null,
            currentUserData?.firstName || '',
            currentUserData?.lastName || '',
          );
          addMeasurement(newMeasurement);
          setInteractionMode(EInteractionMode.NONE);
          setStartPoint(null);
          setTempEndPoint(null);
          setCurrentlySelected(items.find((item) => item.id === 0) || items[0]);
        }
        return;
      }

      if (interactionMode === EInteractionMode.ATTACHMENT) {
        event.stopPropagation();
        // console.log(tempHotspotPosition)
        if (!tempHotspotPosition) return;
        addHotspot({
          position: intersection.clone(),
          creationDate: new Date(),
          userId: currentUserData?.id || null,
        });
        setInteractionMode(EInteractionMode.NONE);
        setCurrentlySelected(items.find((item) => item.id === 0) || items[0]);
        setTempHotspotPosition(null);
      }
    },
    [camera, interactionMode, scene, hotspots, addHotspot, addMeasurement, setInteractionMode, currentUserData, startPoint, setCurrentlySelected, items, measurements, raycaster, tempHotspotPosition],
  );

  const handlePointerMove = useCallback(
    (event: MouseEvent) => {
      const isMeasureMode = interactionMode === EInteractionMode.MEASURE;
      const isAttachmentMode = interactionMode === EInteractionMode.ATTACHMENT;

      // nothing to do if neither measure nor attachment is active
      if (!isMeasureMode && !isAttachmentMode) return;

      const mouse = new THREE.Vector2((event.clientX / window.innerWidth) * 2 - 1, -(event.clientY / window.innerHeight) * 2 + 1);

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      if (intersects.length === 0) {
        if (isAttachmentMode) {
          setTempHotspotPosition(null);
        }
        return;
      }

      const intersection = intersects[0].point;
      intersection.z = 0;

      // MEASURE preview
      if (isMeasureMode && startPoint) {
        if (!intersection.equals(startPoint)) {
          setTempEndPoint(intersection.clone());
        }
      }

      // ATTACHMENT preview position
      if (isAttachmentMode) {
        setTempHotspotPosition(intersection.clone());
      }
    },
    [camera, interactionMode, startPoint, scene, raycaster],
  );

  useEffect(() => {
    const canvas = gl.domElement;
    canvas.addEventListener('pointerdown', handleCanvasClick);
    canvas.addEventListener('pointermove', handlePointerMove);
    return () => {
      canvas.removeEventListener('pointerdown', handleCanvasClick);
      canvas.removeEventListener('pointermove', handlePointerMove);
    };
  }, [gl.domElement, handleCanvasClick, handlePointerMove]);

  useEffect(() => {
    if (!showHotspots) {
      setNewHotspotId(null);
    }
  }, [showHotspots]);


  return (
    <group ref={hotspotGroupRef}>
      {showHotspots && hotspots.map((hotspot) => <Hotspot key={hotspot.id} hotspot={hotspot} onClick={() => handleHotspotClick(hotspot.id)} />)}
      {interactionMode === EInteractionMode.ATTACHMENT && tempHotspotPosition && <PreviewHotspot position={tempHotspotPosition} />}

      {showCameraPositionSprites &&
        cameraPositionSprites.map((camPosSprite) => <CameraPositionSprite key={camPosSprite.id} id={camPosSprite.id} position={camPosSprite.position} />)}
      {showMeasurements &&
        measurements.map((measurement, index) => (
          <SimpleMeasurement
            key={index}
            startPoint={measurement.startPoint}
            endPoint={measurement.endPoint}
            measurementColor={measurement.color}
            selectedSimpleMeasurement={
              selectedSimpleMeasurement && selectedSimpleMeasurement.simpleMeasurementIndex === index ? selectedSimpleMeasurement : ({} as ISelectedSimpleMeasurement)
            }
            onClick={isDrawing ? () => {} : () => handleSimpleMeasurementClick(index)}
            updateMeasurementPosition={(newStartPoint, newEndPoint) => updateMeasurementPosition(measurement.id, newStartPoint, newEndPoint)}
            raycaster={raycaster}
            camera={camera}
            scene={scene}
          />
        ))}
      {interactionMode === EInteractionMode.MEASURE && startPoint && tempEndPoint ? (
        <SimpleMeasurement
          startPoint={startPoint}
          endPoint={tempEndPoint}
          selectedSimpleMeasurement={{} as ISelectedSimpleMeasurement}
          onClick={() => {}}
          isTemporary={true}
          raycaster={raycaster}
          camera={camera}
          scene={scene}
        />
      ) : null}
    </group>
  );
};

export default Annotations;
